// Base Station Screen - Reusable component for all KDS stations
import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  User, 
  Tag, 
  AlertCircle, 
  CheckCircle2, 
  Play, 
  Pause, 
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  RefreshCw
} from 'lucide-react';
import OrderDetailsModal from './OrderDetailsModal';

interface KDSOrderItem {
  id: string;
  item_type: string;
  service_type: string;
  quantity: number;
  unit_price: number;
  notes?: string;
}

interface KDSCustomer {
  id: string;
  name: string;
  phone?: string;
  email?: string;
}

interface KDSOrder {
  id: string;
  customer_id: string;
  status: string;
  total_amount: number;
  paid_amount: number;
  notes?: string;
  due_date?: string;
  created_at: string;
  updated_at: string;
  customers: KDSCustomer;
  order_items: KDSOrderItem[];
  // Legacy fields for compatibility
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  services?: string[];
  special_instructions?: string;
  rfid_tags?: string[];
  processing_minutes?: number;
  staff_name?: string;
  station_notes?: string;
  delivery_address?: string;
  // KDS specific fields
  workflow_id?: string;
  priority_level?: number;
  order_id?: string;
}

interface StationData {
  station: string;
  station_name: string;
  orders: KDSOrder[];
  count: number;
  icon: string;
  color: string;
}

interface BaseStationScreenProps {
  stationId: string;
  stationName: string;
  stationIcon: React.ReactNode;
  stationColor: string;
  showAdvanceButton?: boolean;
  showCustomerContact?: boolean;
  showDeliveryInfo?: boolean;
}

const BaseStationScreen: React.FC<BaseStationScreenProps> = ({
  stationId,
  stationName,
  stationIcon,
  stationColor,
  showAdvanceButton = true,
  showCustomerContact = false,
  showDeliveryInfo = false
}) => {
  const [stationData, setStationData] = useState<StationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingOrders, setProcessingOrders] = useState<Set<string>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Parse SmartDry Core Services from order item notes
  const parseOrderItemServices = (orderItem: KDSOrderItem) => {
    if (!orderItem.notes) return null;
    
    try {
      const parsed = JSON.parse(orderItem.notes);
      return {
        selectedServices: parsed.selectedServices || [],
        selectedAddons: parsed.selectedAddons || [],
        selectedSpecialties: parsed.selectedSpecialties || [],
        rfidTags: parsed.rfidTags || [],
        issues: parsed.issues || [],
        color: parsed.color || null,
        serviceLevel: parsed.service_level || null
      };
    } catch {
      return null;
    }
  };

  const fetchStationData = async () => {
    try {
      setError(null);
      
      // Get business data from TDS session
      const businessData = localStorage.getItem('kds_business_data');
      const tdsCode = localStorage.getItem('kds_tds_code');
      let businessId = '';
      
      if (businessData) {
        const parsedBusinessData = JSON.parse(businessData);
        businessId = parsedBusinessData.businessId;
      }
      
      // Include business filtering in API calls
      const queryParams = businessId ? `?business_id=${businessId}&tds_code=${tdsCode}` : '';
      let response = await fetch(`/api/kds/station/${stationId}${queryParams}`);
      
      // If unauthorized, try with auth token
      if (response.status === 401) {
        response = await fetch(`/api/kds/station/${stationId}${queryParams}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'dev-token'}`,
            'X-TDS-Code': tdsCode || '',
            'X-Business-ID': businessId || ''
          }
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStationData(data.data);
    } catch (error) {
      console.error(`Failed to fetch ${stationName} data:`, error);
      setError(`Failed to load ${stationName} orders - Make sure backend server is running`);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: string, status: string, notes?: string) => {
    try {
      setProcessingOrders(prev => new Set(prev).add(orderId));
      
      const response = await fetch(`/api/kds/status/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          status,
          notes: notes || `${stationName} status updated to ${status}`
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update order status`);
      }

      // Refresh station data
      await fetchStationData();
    } catch (error) {
      console.error('Failed to update order status:', error);
      alert('Failed to update order status. Please try again.');
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const advanceOrder = async (orderId: string) => {
    try {
      setProcessingOrders(prev => new Set(prev).add(orderId));
      
      const response = await fetch(`/api/kds/advance/${orderId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          notes: `Completed at ${stationName} station`
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to advance order`);
      }

      const result = await response.json();
      console.log('Order advanced:', result.data);

      // Refresh station data
      await fetchStationData();
    } catch (error) {
      console.error('Failed to advance order:', error);
      alert('Failed to advance order. Please try again.');
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchStationData();
    
    // Refresh every 30 seconds (reduced frequency)
    const interval = setInterval(fetchStationData, 30000);
    return () => clearInterval(interval);
  }, [stationId]);

  const getPriorityColor = (level: number) => {
    switch (level) {
      case 3: return 'bg-red-100 text-red-800 border-red-200';
      case 2: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const handleOrderClick = (order: any) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleCloseOrderDetails = () => {
    setSelectedOrder(null);
    setShowOrderDetails(false);
  };

  const getPriorityLabel = (level: number) => {
    switch (level) {
      case 3: return 'Rush';
      case 2: return 'Urgent';
      default: return 'Normal';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'on_hold': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-lg text-gray-600">Loading {stationName}...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-800">Error Loading Station Data</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button 
                onClick={fetchStationData}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Station Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div 
              className="p-3 rounded-lg"
              style={{ backgroundColor: `${stationColor}20`, color: stationColor }}
            >
              {stationIcon}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{stationName} Station</h1>
              <p className="text-gray-600">{stationData?.count || 0} orders in queue</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={fetchStationData}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            
            <div className="text-right">
              <div className="text-sm font-medium text-gray-900">Last Updated</div>
              <div className="text-xs text-gray-500">{new Date().toLocaleTimeString()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      {stationData?.count === 0 ? (
        <div className="text-center py-12">
          <div 
            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: `${stationColor}20`, color: stationColor }}
          >
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h3>
          <p className="text-gray-600">No orders in the {stationName.toLowerCase()} queue right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {stationData?.orders.map((order) => (
            <div
              key={order.workflow_id}
              className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md cursor-pointer ${
                (order.priority_level || 1) > 1 ? 'border-l-4 border-l-red-500' : 'border-gray-200'
              }`}
              onClick={() => handleOrderClick(order)}
            >
              <div className="p-4">
                {/* Order Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      #{order.id?.slice(-6) || 'N/A'}
                    </div>
                    <span className="text-xs text-gray-500">Order Number</span>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(order.priority_level || 1)}`}>
                    {getPriorityLabel(order.priority_level || 1)}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="mb-3">
                  <h3 className="font-semibold text-gray-900">{order.customers?.name || order.customer_name || 'Unknown Customer'}</h3>
                  {showCustomerContact && (
                    <div className="mt-1 space-y-1">
                      {(order.customers?.phone || order.customer_phone) && (
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <Phone className="w-3 h-3" />
                          <span>{order.customers?.phone || order.customer_phone}</span>
                        </div>
                      )}
                      {(order.customers?.email || order.customer_email) && (
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <Mail className="w-3 h-3" />
                          <span>{order.customers?.email || order.customer_email}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {showDeliveryInfo && order.delivery_address && (
                    <div className="flex items-center space-x-1 text-sm text-gray-600 mt-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{order.delivery_address}</span>
                    </div>
                  )}
                </div>

                {/* Service Level Priority */}
                {(() => {
                  let serviceLevel = null;
                  
                  // Check for service level in order items
                  order.order_items?.forEach((item: any) => {
                    const parsedServices = parseOrderItemServices(item);
                    if (parsedServices && parsedServices.serviceLevel) {
                      serviceLevel = parsedServices.serviceLevel;
                    }
                  });
                  
                  if (serviceLevel && serviceLevel !== 'standard') {
                    const levelInfo: Record<string, { name: string; color: string; icon: string }> = {
                      express: { name: 'Express', color: 'bg-yellow-100 text-yellow-800', icon: '⚡' },
                      rush: { name: 'Rush', color: 'bg-red-100 text-red-800', icon: '🔥' }
                    };
                    
                    const info = levelInfo[serviceLevel];
                    return info ? (
                      <div className="mb-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${info.color}`}>
                          {info.icon} {info.name} Service
                        </span>
                      </div>
                    ) : null;
                  }
                  
                  return null;
                })()}

                {/* Services - Enhanced with SmartDry Core Services */}
                <div className="mb-3">
                  {(() => {
                    // Parse SmartDry Core Services from order items
                    const allServices = new Set<string>();
                    const allAddons = new Set<string>();
                    const allSpecialties = new Set<string>();
                    let hasStainsOrIssues = false;
                    let hasReportedIssues = false;
                    
                    order.order_items?.forEach((item: any) => {
                      const parsedServices = parseOrderItemServices(item);
                      
                      if (parsedServices) {
                        parsedServices.selectedServices.forEach((s: any) => allServices.add(s.name));
                        parsedServices.selectedAddons.forEach((a: any) => allAddons.add(a.name));
                        parsedServices.selectedSpecialties.forEach((sp: any) => allSpecialties.add(sp.name));
                        if (parsedServices.issues.length > 0) hasStainsOrIssues = true;
                      }
                    });
                    
                    // Check for reported issues (workflow stage = issue_reported)
                    if (order.notes) {
                      try {
                        const orderNotes = JSON.parse(order.notes);
                        if (orderNotes.workflow_stage === 'issue_reported') {
                          hasReportedIssues = true;
                        }
                      } catch {
                        if (order.notes.includes('workflow_stage:issue_reported')) {
                          hasReportedIssues = true;
                        }
                      }
                    }
                    
                    return (
                      <div className="space-y-2">
                        {/* Core Services with Station Context */}
                        {allServices.size > 0 && (
                          <div>
                            <div className="text-xs text-gray-600 mb-1 font-medium">
                              Selected Services:
                              {stationId === 'processing' && ' (Ready to Begin)'}
                              {stationId === 'wash-dry' && ' (Active Processing)'}
                              {stationId === 'fold-press' && ' (Final Stage)'}
                              {stationId === 'stain-removal' && ' (Special Treatment)'}
                              {stationId === 'alteration' && ' (Modification Required)'}
                              {stationId === 'pickup' && ' (Completed Services)'}
                              {stationId === 'delivery' && ' (Ready for Delivery)'}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Array.from(allServices).map((service, index) => (
                                <span 
                                  key={index}
                                  className={`px-2 py-1 text-xs rounded-full font-medium ${
                                    stationId === 'processing' || stationId === 'wash-dry' || stationId === 'fold-press'
                                      ? 'bg-green-200 text-green-900 border border-green-300'
                                      : 'bg-green-100 text-green-800'
                                  }`}
                                >
                                  🧵 {service}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Add-ons - ESSENTIAL INFORMATION with Station Context */}
                        {allAddons.size > 0 && (
                          <div className="bg-purple-50 p-2 rounded border border-purple-200">
                            <div className="text-xs text-purple-700 mb-1 font-bold">
                              🚨 ESSENTIAL: Selected Add-ons 
                              {stationId === 'wash-dry' && ' (Apply During Processing)'}
                              {stationId === 'fold-press' && ' (Apply During Finishing)'}
                              {stationId === 'stain-removal' && ' (Consider for Treatment)'}
                              {stationId === 'alteration' && ' (Note for Completion)'}
                              {stationId === 'pickup' && ' (Applied Services)'}
                              {stationId === 'delivery' && ' (Applied Services)'}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Array.from(allAddons).map((addon, index) => (
                                <span 
                                  key={index}
                                  className={`px-2 py-1 text-xs rounded-full font-bold border ${
                                    stationId === 'wash-dry' || stationId === 'fold-press' 
                                      ? 'bg-purple-200 text-purple-900 border-purple-300' 
                                      : 'bg-purple-100 text-purple-800 border-purple-200'
                                  }`}
                                >
                                  ➕ {addon}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Specialties with Station Context */}
                        {allSpecialties.size > 0 && (
                          <div className={`${
                            stationId === 'stain-removal' || stationId === 'issues' 
                              ? 'bg-orange-50 p-2 rounded border border-orange-200' 
                              : ''
                          }`}>
                            <div className="text-xs text-orange-600 mb-1 font-medium">
                              Special Handling Required:
                              {stationId === 'stain-removal' && ' (PRIMARY FOCUS)'}
                              {stationId === 'issues' && ' (PROBLEM ITEMS)'}
                              {stationId === 'wash-dry' && ' (Handle with Care)'}
                              {stationId === 'fold-press' && ' (Note for Finishing)'}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Array.from(allSpecialties).map((specialty, index) => (
                                <span 
                                  key={index}
                                  className={`px-2 py-1 text-xs rounded-full font-medium ${
                                    stationId === 'stain-removal' || stationId === 'issues'
                                      ? 'bg-orange-200 text-orange-900 border border-orange-300'
                                      : 'bg-orange-100 text-orange-800'
                                  }`}
                                >
                                  🔸 {specialty}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Reported Issues Alert */}
                        {hasReportedIssues && (
                          <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-2 py-1 rounded">
                            <AlertCircle className="w-3 h-3" />
                            <span className="text-xs font-medium">⚠️ Issues Reported by Staff</span>
                          </div>
                        )}
                        
                        {/* Stains/Issues Detected */}
                        {hasStainsOrIssues && !hasReportedIssues && (
                          <div className="flex items-center space-x-1 text-orange-600 bg-orange-50 px-2 py-1 rounded">
                            <AlertCircle className="w-3 h-3" />
                            <span className="text-xs font-medium">🔍 Stains/Issues Detected</span>
                          </div>
                        )}
                        
                        {/* Fallback to basic services if no parsed data */}
                        {allServices.size === 0 && allAddons.size === 0 && allSpecialties.size === 0 && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1 font-medium">Service Type:</div>
                            <div className="flex flex-wrap gap-1">
                              {order.services && order.services.length > 0 ? (
                                order.services.map((service, index) => (
                                  <span 
                                    key={index}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                                  >
                                    {service}
                                  </span>
                                ))
                              ) : (
                                // Show basic service info from order items
                                order.order_items?.map((item, index) => (
                                  <span 
                                    key={index}
                                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                  >
                                    🧵 {item.service_type || item.item_type}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Special Instructions */}
                {order.special_instructions && (
                  <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-800">{order.special_instructions}</p>
                  </div>
                )}

                {/* RFID Tags - Enhanced with parsed data */}
                {(() => {
                  const allRfidTags = new Set<string>();
                  
                  // Collect RFID tags from order items
                  order.order_items?.forEach((item: any) => {
                    const parsedServices = parseOrderItemServices(item);
                    if (parsedServices && parsedServices.rfidTags.length > 0) {
                      parsedServices.rfidTags.forEach((tag: string) => allRfidTags.add(tag));
                    }
                  });
                  
                  // Fallback to order-level RFID tags
                  if (allRfidTags.size === 0 && order.rfid_tags && order.rfid_tags.length > 0) {
                    order.rfid_tags.forEach((tag: string) => allRfidTags.add(tag));
                  }
                  
                  // Generate placeholder RFID tags if none found (for demo purposes)
                  if (allRfidTags.size === 0) {
                    order.order_items?.forEach((_item: any, _index: number) => {
                      const placeholderTag = `${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
                      allRfidTags.add(placeholderTag);
                    });
                  }
                  
                  return allRfidTags.size > 0 ? (
                    <div className="mb-3">
                      <div className="flex items-center space-x-1 mb-1">
                        <Tag className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">RFID Tags:</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Array.from(allRfidTags).map((tag, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-mono border"
                          >
                            🏷️ {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Time and Status */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>{order.processing_minutes ? `${Math.round(order.processing_minutes)}m` : '0m'}</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                      {order.status.replace('_', ' ')}
                    </div>
                  </div>
                  {order.staff_name && (
                    <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                      <User className="w-3 h-3" />
                      <span>{order.staff_name}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  {order.status === 'at_station' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'in_progress')}
                      disabled={processingOrders.has(order.id)}
                      className="w-full flex items-center justify-center space-x-2 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span>Start Work</span>
                    </button>
                  )}

                  {order.status === 'in_progress' && (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => updateOrderStatus(order.id, 'on_hold', 'Work paused')}
                        disabled={processingOrders.has(order.id)}
                        className="flex items-center justify-center space-x-1 py-2 px-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                      >
                        <Pause className="w-3 h-3" />
                        <span className="text-sm">Pause</span>
                      </button>
                      
                      {showAdvanceButton && (
                        <button
                          onClick={() => advanceOrder(order.id)}
                          disabled={processingOrders.has(order.id)}
                          className="flex items-center justify-center space-x-1 py-2 px-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          <ArrowRight className="w-3 h-3" />
                          <span className="text-sm">Complete</span>
                        </button>
                      )}
                    </div>
                  )}

                  {order.status === 'on_hold' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'in_progress')}
                      disabled={processingOrders.has(order.id)}
                      className="w-full flex items-center justify-center space-x-2 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span>Resume Work</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Order Details Modal */}
      <OrderDetailsModal
        isOpen={showOrderDetails}
        onClose={handleCloseOrderDetails}
        order={selectedOrder}
        onOrderUpdate={fetchStationData}
        stationId={stationId}
      />
    </div>
  );
};

export default BaseStationScreen;
